import React, { useState, useEffect } from 'react';

import { Button, Card, Flex, Link, Text } from '@chakra-ui/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';

import { Alert } from '@firehydrant/design-system';
import OrgRouteLink from 'components/common/OrgRouteLink';
import {
  useNotificationPolicy,
  useUserPolicyCompliance,
} from 'queries/notificationPolicy';
import { useSignalDestinations } from 'queries/signalsNotifications';

import { ComplianceWarning } from './NotificationPolicy/ComplianceWarning';
import PolicyComplianceCard from './NotificationPolicy/PolicyComplianceCard';
import { PolicyRequirements } from './NotificationPolicy/PolicyRequirements';
import PhoneVerificationModal from './PhoneVerificationModal';
import SignalsNotificationsForm from './SignalsNotificationForm';

const PhonePromptMessage = ({ refetchDestinations }) => {
  const [verifyNumber, setVerifyNumber] = useState();

  return (
    <>
      <Flex flexDir="column" mt="4">
        <Alert variant="warning" mb="4">
          <Text textStyle="heading3" color="content.feedback.critical.primary">
            You need a verified phone number added to your account to receive
            text and voice notifications.
          </Text>
          <Button
            size="sm"
            mt="3"
            onClick={() => setVerifyNumber({ open: true })}
            variant="outline"
          >
            Add a phone number
          </Button>
        </Alert>
      </Flex>
      <PhoneVerificationModal
        refetch={refetchDestinations}
        reverify={false}
        setReverify={() => {}}
        verifyNumber={verifyNumber}
        setVerifyNumber={setVerifyNumber}
      />
    </>
  );
};

const SignalsNotificationSettings = ({
  readOnly = false,
  notificationPolicyComplianceStatus,
}) => {
  const { signalsOrganizationNotificationPolicy } = useFlags();
  const { data: destinations, refetch } = useSignalDestinations();
  const { data: policy } = useNotificationPolicy();
  const { data: userPolicyCompliance, refetch: refetchUserPolicyCompliance } =
    useUserPolicyCompliance({
      enabled: !readOnly && signalsOrganizationNotificationPolicy,
    });

  // Use the appropriate compliance data based on readOnly mode
  const complianceData = readOnly
    ? notificationPolicyComplianceStatus
    : userPolicyCompliance;

  // Helper function to check if a policy item is compliant
  const isStepCompliantById = (stepId, complianceData) => {
    const compliance = complianceData?.find(
      (item) => item.notification_policy_item_id === stepId,
    );
    return compliance?.is_compliant ?? false;
  };

  // Check if user has any non-compliant steps for a priority
  const hasPriorityNonCompliance = (policyPriority, complianceData) => {
    if (!policyPriority?.steps?.length || !complianceData) return false;

    return policyPriority.steps.some(
      (step) => !isStepCompliantById(step.id, complianceData),
    );
  };

  // Convert compliance data to the format expected by PolicyRequirements component
  const getPriorityStepCompliance = (policyPriority, complianceData) => {
    if (!policyPriority?.steps?.length || !complianceData) return [];

    return policyPriority.steps.map((step, stepIndex) => ({
      stepIndex,
      isCompliant: isStepCompliantById(step.id, complianceData),
    }));
  };

  const highPriorityPolicy = policy?.priorities?.find(
    (p) => p.priority === 'HIGH',
  );
  const medPriorityPolicy = policy?.priorities?.find(
    (p) => p.priority === 'MEDIUM',
  );
  const lowPriorityPolicy = policy?.priorities?.find(
    (p) => p.priority === 'LOW',
  );

  const highStepCompliance = getPriorityStepCompliance(
    highPriorityPolicy,
    complianceData,
  );
  const medStepCompliance = getPriorityStepCompliance(
    medPriorityPolicy,
    complianceData,
  );
  const lowStepCompliance = getPriorityStepCompliance(
    lowPriorityPolicy,
    complianceData,
  );

  const hasHighNonCompliance = hasPriorityNonCompliance(
    highPriorityPolicy,
    complianceData,
  );
  const hasMedNonCompliance = hasPriorityNonCompliance(
    medPriorityPolicy,
    complianceData,
  );
  const hasLowNonCompliance = hasPriorityNonCompliance(
    lowPriorityPolicy,
    complianceData,
  );

  const hasAnyNonCompliance =
    hasHighNonCompliance || hasMedNonCompliance || hasLowNonCompliance;

  // Auto-expand policy details if user is not compliant
  const [showPolicyDetails, setShowPolicyDetails] = useState(false);

  // Auto-expand policy details when user becomes non-compliant
  useEffect(() => {
    if (hasAnyNonCompliance && policy) {
      setShowPolicyDetails(true);
    }
  }, [hasAnyNonCompliance, policy]);

  const noVerifiedPhone =
    destinations?.filter((destination) => destination.type === 'phone')
      .length === 0;
  return (
    <>
      <Flex flexDir="column" my="4" maxW="900px">
        <Text textStyle="heading3" mb="2">
          Alert Notifications
        </Text>
        <Text textStyle="body1" color="content.primary">
          Choose a notification type and time to configure how you are notified
          when an alert is assigned to you. Add phone numbers, additional
          emails, and link third-party accounts in the{' '}
          <Link to="/account/edit" as={OrgRouteLink}>
            Profile
          </Link>{' '}
          tab.
        </Text>
        {noVerifiedPhone && !readOnly && (
          <PhonePromptMessage refetchDestinations={refetch} />
        )}

        {/* Show compliance warning if policy exists and user is not compliant */}
        {policy && hasAnyNonCompliance && (
          <ComplianceWarning variant="warning" showPolicyLink={true} />
        )}

        {/* Show link to view policy requirements */}
        {policy && !hasAnyNonCompliance && (
          <Flex mt="3">
            <Button
              size="sm"
              variant="link"
              onClick={() => setShowPolicyDetails(!showPolicyDetails)}
            >
              {showPolicyDetails ? 'Hide' : 'View'} organization policy
              requirements
            </Button>
          </Flex>
        )}
      </Flex>

      {/* Show policy requirements details when expanded */}
      {policy && showPolicyDetails && (
        <Flex flexDir="column" mb="4" gap="4">
          {readOnly ? (
            /* Show read-only compliance overview */
            <PolicyComplianceCard
              policy={policy}
              complianceStatus={complianceData}
            />
          ) : (
            /* Show detailed policy requirements with user preferences */
            <>
              <Text textStyle="heading3">Organization Policy Requirements</Text>
              <Flex
                flexDir="row"
                justifyContent="space-between"
                flexWrap={{ base: 'wrap', lg: 'nowrap' }}
                gap="2"
              >
                {highPriorityPolicy && (
                  <Card
                    width={{ base: '100%', lg: '33%' }}
                    p="3"
                    border="1px solid"
                    borderColor="stroke.primary"
                    boxShadow="sm"
                  >
                    <PolicyRequirements
                      policyPriority={highPriorityPolicy}
                      complianceStatus={highStepCompliance}
                    />
                  </Card>
                )}
                {medPriorityPolicy && (
                  <Card
                    width={{ base: '100%', lg: '33%' }}
                    p="3"
                    border="1px solid"
                    borderColor="stroke.primary"
                    boxShadow="sm"
                  >
                    <PolicyRequirements
                      policyPriority={medPriorityPolicy}
                      complianceStatus={medStepCompliance}
                    />
                  </Card>
                )}
                {lowPriorityPolicy && (
                  <Card
                    width={{ base: '100%', lg: '33%' }}
                    p="3"
                    border="1px solid"
                    borderColor="stroke.primary"
                    boxShadow="sm"
                  >
                    <PolicyRequirements
                      policyPriority={lowPriorityPolicy}
                      complianceStatus={lowStepCompliance}
                    />
                  </Card>
                )}
              </Flex>
            </>
          )}
        </Flex>
      )}

      <Flex
        flexDir="row"
        justifyContent="space-between"
        flexWrap={{ base: 'wrap', lg: 'nowrap' }}
        gap="2"
      >
        <Flex
          flexDir="column"
          bgColor={
            hasHighNonCompliance && policy
              ? 'surface.feedback.error.soft'
              : 'surface.primary'
          }
          border="1px"
          borderColor={
            hasHighNonCompliance && policy ? 'stroke.red' : 'stroke.primary'
          }
          width={{ base: '100%', lg: '33%' }}
          borderRadius={10}
          p="2"
          boxShadow="sm"
        >
          <Flex justifyContent="space-between" alignItems="center" mb="2">
            <Text textStyle="heading3" ml="0.5">
              High Priority
            </Text>
            {policy && hasHighNonCompliance && (
              <Text textStyle="body2" color="content.feedback.error.primary">
                Not in compliance
              </Text>
            )}
          </Flex>

          <SignalsNotificationsForm
            notificationPriority="HIGH"
            destinations={destinations}
            readOnly={readOnly}
            refetchCompliance={refetchUserPolicyCompliance}
          />
        </Flex>
        <Flex
          flexDir="column"
          bgColor={
            hasMedNonCompliance && policy
              ? 'surface.feedback.error.soft'
              : 'surface.primary'
          }
          border="1px"
          borderColor={
            hasMedNonCompliance && policy ? 'stroke.red' : 'stroke.primary'
          }
          width={{ base: '100%', lg: '33%' }}
          borderRadius={10}
          p="2"
          boxShadow="sm"
        >
          <Flex justifyContent="space-between" alignItems="center" mb="2">
            <Text textStyle="heading3" ml="0.5">
              Medium Priority
            </Text>
            {policy && hasMedNonCompliance && (
              <Text textStyle="body2" color="content.feedback.error.primary">
                Not in compliance
              </Text>
            )}
          </Flex>
          <SignalsNotificationsForm
            notificationPriority="MEDIUM"
            destinations={destinations}
            readOnly={readOnly}
            refetchCompliance={refetchUserPolicyCompliance}
          />
        </Flex>
        <Flex
          flexDir="column"
          bgColor={
            hasLowNonCompliance && policy
              ? 'surface.feedback.error.soft'
              : 'surface.primary'
          }
          border="1px"
          borderColor={
            hasLowNonCompliance && policy ? 'stroke.red' : 'stroke.primary'
          }
          width={{ base: '100%', lg: '33%' }}
          borderRadius={10}
          p="2"
          boxShadow="sm"
        >
          <Flex justifyContent="space-between" alignItems="center" mb="2">
            <Text textStyle="heading3" ml="0.5">
              Low Priority
            </Text>
            {policy && hasLowNonCompliance && (
              <Text textStyle="body2" color="content.feedback.error.primary">
                Not in compliance
              </Text>
            )}
          </Flex>
          <SignalsNotificationsForm
            notificationPriority="LOW"
            destinations={destinations}
            readOnly={readOnly}
            refetchCompliance={refetchUserPolicyCompliance}
          />
        </Flex>
      </Flex>
    </>
  );
};

export default SignalsNotificationSettings;

PhonePromptMessage.propTypes = {
  refetchDestinations: PropTypes.func.isRequired,
};

SignalsNotificationSettings.propTypes = {
  readOnly: PropTypes.bool,
  notificationPolicyComplianceStatus: PropTypes.arrayOf(
    PropTypes.shape({
      is_compliant: PropTypes.bool.isRequired,
      notification_policy_item_id: PropTypes.string.isRequired,
    }),
  ),
};
